import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ShieldCheck } from "lucide-react";
import { gbp, miles, imgUrl, STATUS_LABELS } from "@/lib/api";
import { useSite } from "@/lib/site";

const BADGE = {
  just_arrived: "bg-forest text-paper",
  reserved: "bg-brass text-paper",
  coming_soon: "bg-stone text-charcoal",
  sold: "bg-charcoal text-paper",
};

export const StatusBadge = ({ status, className = "" }) =>
  STATUS_LABELS[status] ? (
    <span data-testid={`status-badge-${status}`} className={`eyebrow inline-block px-3 py-1.5 !text-[0.62rem] ${BADGE[status]} ${className}`}>{STATUS_LABELS[status]}</span>
  ) : null;

export const LabelBadge = ({ label, className = "" }) =>
  label ? <span data-testid="vehicle-label-badge" className={`eyebrow inline-block bg-paper px-3 py-1.5 !text-[0.62rem] text-forest shadow-sm ${className}`}>{label}</span> : null;

const prepared = (v) => (v.preparation_items || []).length > 0 && v.status !== "coming_soon";

export default function VehicleCard({ v, index = 0 }) {
  const finance = useSite().finance_enabled;
  if (!v) return null;
  return (
    <motion.article
      initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.8, delay: (index % 3) * 0.08, ease: [0.2, 0.7, 0.2, 1] }}
      className="group" data-testid={`vehicle-card-${v.slug}`}
    >
      <Link to={`/stock/${v.slug}`} className="block" aria-label={`View ${v.year} ${v.make} ${v.model} ${v.derivative}`}>
        <div className="img-zoom relative aspect-[4/3] overflow-hidden bg-stone">
          <img src={imgUrl(v.images?.[0])} alt={`${v.year} ${v.make} ${v.model} in ${v.colour}`} loading="lazy" className={`h-full w-full object-cover ${v.status === "sold" ? "grayscale-[40%]" : ""}`} />
          <div className="absolute left-4 top-4 flex flex-wrap gap-2">
            <StatusBadge status={v.status} />
            <LabelBadge label={v.badge} />
          </div>
          {prepared(v) && (
            <span className="absolute bottom-0 left-0 flex items-center gap-2 bg-paper/95 px-3 py-2 text-[0.62rem] font-bold uppercase tracking-[0.14em] text-forest" data-testid="standard-badge">
              <ShieldCheck className="h-3.5 w-3.5" strokeWidth={1.75} />Prepared to the Cirencester Standard
            </span>
          )}
          <span className="absolute bottom-4 right-4 grid h-11 w-11 translate-y-2 place-items-center rounded-full bg-paper text-forest opacity-0 transition duration-500 group-hover:translate-y-0 group-hover:opacity-100">
            <ArrowUpRight className="h-5 w-5" strokeWidth={1.5} />
          </span>
        </div>
        <div className="border-b border-stone pb-6 pt-5 transition-colors duration-500 group-hover:border-forest/50">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="eyebrow text-charcoal/70">{v.year} · {v.make}</p>
              <h3 className="mt-1 font-serif text-[1.75rem] leading-tight text-charcoal">{v.model}</h3>
              <p className="truncate text-sm text-charcoal/75">{v.derivative}</p>
            </div>
            <div className="shrink-0 text-right">
              <p className="font-serif text-[1.75rem] leading-tight text-forest" data-testid={`vehicle-price-${v.slug}`}>{v.status === "sold" && v.price == null ? "Sold" : gbp(v.price)}</p>
              {finance && v.finance_monthly && v.status !== "sold" && <p className="text-xs text-charcoal/70">or from {gbp(v.finance_monthly)}/mo*</p>}
            </div>
          </div>
          <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[0.8rem] text-charcoal/80">
            <li>{miles(v.mileage)}</li><li>{v.fuel_type}</li><li>{v.transmission}</li>{v.seats && <li>{v.seats} seats</li>}
          </ul>
          {v.short_description && <p className="mt-3 line-clamp-1 text-sm text-charcoal/70">{v.short_description}</p>}
          <div className="mt-5 flex items-center justify-between">
            <span className="text-xs text-charcoal/70">{v.colour}{v.towing_capacity ? ` · Tows ${v.towing_capacity.toLocaleString("en-GB")}kg` : ""}</span>
            <span className="link-u text-xs font-semibold uppercase tracking-[0.14em] text-charcoal">View Vehicle</span>
          </div>
        </div>
      </Link>
    </motion.article>
  );
}
